// @ts-check

import { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { gql, useLazyQuery } from '@apollo/client'
import styled from 'styled-components'

import Flexer from 'js/components/Flexer/Flexer'
import FormItem from 'js/components/Form/FormItem'
import IconButton from 'js/components/IconButton/IconButton'
import Spacer from 'js/components/Spacer/Spacer'
import { MAX_LENGTH, REGEX } from 'js/lib/constants'

import CheckIcon from 'icons/check.svg'
import CrossIcon from 'icons/cross.svg'
import EditIcon from 'icons/edit-small.svg'

const Wrapper = styled(Spacer)`
    .slug-field--wrapper {
        display: flex;
        align-items: center;
        font-size: ${(p) => p.theme.font.size.tiny};
        line-height: ${(p) => p.theme.font.lineHeight.tiny};
    }

    .slug-field--url {
        margin-left: 4px;
        color: ${(p) => p.theme.color.text.grey};
    }

    .slug-field--form {
        margin-top: 16px;
        flex-grow: 1;
        display: flex;
        align-items: flex-start;

        .slug-field--form-text-field {
            flex-grow: 1;
            margin-right: 8px;
        }

        .slug-field--form-url {
            height: 100%;
            display: flex;
            align-items: center;
            padding: 0 8px 0 12px;
            font-size: ${(p) => p.theme.font.size.tiny};
            line-height: ${(p) => p.theme.font.lineHeight.tiny};
            color: ${(p) => p.theme.color.text.grey};
            border-right: 1px solid ${(p) => p.theme.color.grey[30]};
        }
    }
`

const CustomSlugForm = ({ defaultValue, setIsEditing, onSubmit }) => {
    const { t } = useTranslation()

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, errors: localErrors },
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            customSlug: defaultValue,
        },
    })

    const handleSubmitForm = ({ customSlug }) => {
        onSubmit(customSlug)
    }

    const submitForm = handleSubmit(handleSubmitForm)

    /**
     * @param {React.KeyboardEvent<HTMLInputElement>} event - The keydown event
     */
    const handleOnKeyDown = (event) => {
        // Allow traversing, editing the field with meta, arrow and edit keys
        if (
            REGEX.navKey.test(event.key) ||
            REGEX.editKey.test(event.key) ||
            REGEX.metaKey.test(event.key)
        )
            return
        // Allow only safe characters for slugs
        if (!REGEX.slugInputChar.test(event.key)) event.preventDefault()
        // Submit on Enter
        if (event.key === 'Enter') return submitForm()
    }

    return (
        <div className="slug-field--form">
            <FormItem
                size="small"
                control={control}
                type="text"
                name="customSlug"
                label={t('form.url')}
                aria-label={t('form.custom-url')}
                helper={t('form.custom-url-helper')}
                errors={localErrors}
                maxLength={MAX_LENGTH.title}
                rules={{
                    pattern: {
                        value: REGEX.slug,
                        message: t('error.invalid-slug'),
                    },
                }}
                ElementBefore={
                    <span className="slug-field--form-url">
                        {window.location.host}/
                    </span>
                }
                className="slug-field--form-text-field"
                onKeyDown={handleOnKeyDown}
                formatValue={(value) => {
                    const newValue = value
                        .trimStart()
                        .replace(/ /g, '-') // Replace space by hyphen
                        .replace(/--+/g, '-') // Remove double hyphen
                        .toLowerCase()
                    return newValue
                }}
            />
            <Flexer gutter="tiny" divider="normal" style={{ marginTop: 2 }}>
                <IconButton
                    size="normal"
                    variant={isDirty ? 'primary' : 'tertiary'}
                    radiusStyle="rounded"
                    tooltip={t('action.save')}
                    aria-label={t('form.save-custom-url')}
                    onClick={submitForm}
                    disabled={!isValid || !isDirty}
                >
                    <CheckIcon />
                </IconButton>
                <IconButton
                    size="normal"
                    variant="secondary"
                    radiusStyle="rounded"
                    tooltip={t('action.cancel')}
                    aria-label={t('form.cancel-custom-url')}
                    onClick={() => {
                        setIsEditing(false)
                    }}
                >
                    <CrossIcon />
                </IconButton>
            </Flexer>
        </div>
    )
}

const TitleSlugField = ({ entity, urlSlugEnabled }) => {
    const { t } = useTranslation()

    const {
        control,
        getValues,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext()

    const watchUrlSlug = watch('urlSlug')

    const [getSlugSuggestion, { loading, data }] =
        useLazyQuery(GET_SLUG_SUGGESTION)

    useEffect(() => {
        const slugSuggestion = data?.entitySlugSuggestion
        if (slugSuggestion?.available) {
            setValue('urlSlug', slugSuggestion.suggestion) // Set slug
        }
    }, [data, setValue, getValues])

    const handleOnBlurTitle = (e) => {
        const value = e.target.value
        const newValue = value.trim().replace(/\s\s+/g, ' ') // Remove trailing and double spaces

        setValue('title', newValue)

        if (!urlSlugEnabled) return

        // Don't overwrite if entity slug exists or if custom slug is set
        if (getValues('urlSlugIsCustom') || entity?.urlSlug) return

        getSlugSuggestion({
            variables: {
                guid: entity?.guid,
                title: newValue,
            },
        })
    }

    const handleOnSubmitSlug = (val) => {
        const customSlug = val
        const urlSlug = entity?.urlSlug

        // If a custom slug is set and differs from entity slug, ask for confirmation
        if (!!customSlug && !!urlSlug && customSlug !== urlSlug) {
            if (!confirm(t('form.custom-url-confirm'))) return
        }

        setValue('urlSlugIsCustom', !!customSlug) // Keep track of whether the slug is custom or not
        getSlugSuggestion({
            variables: {
                guid: entity?.guid,
                title:
                    customSlug || // Get slug suggestion based on input
                    entity?.urlSlug || // ..if empty use existing urlSlug
                    getValues('title'), // ..or get slug based on title
            },
        })
        setIsEditing(false)
    }

    const [isEditing, setIsEditing] = useState(false)

    return (
        <Wrapper spacing="tiny">
            <FormItem
                control={control}
                type="text"
                name="title"
                title={t('form.title')}
                required
                errors={errors}
                onBlur={handleOnBlurTitle}
            />

            {urlSlugEnabled && (
                <>
                    {isEditing ? (
                        <CustomSlugForm
                            defaultValue={getValues('urlSlug')}
                            setIsEditing={setIsEditing}
                            onSubmit={handleOnSubmitSlug}
                        />
                    ) : (
                        <div className="slug-field--wrapper">
                            <span>{t('form.url')}:</span>
                            <span className="slug-field--url">
                                {window.location.host}/{watchUrlSlug}
                            </span>
                            <IconButton
                                size="small"
                                variant="secondary"
                                radiusStyle="rounded"
                                tooltip={t('action.edit')}
                                aria-label={t('form.edit-custom-url')}
                                onClick={() => {
                                    setIsEditing(true)
                                }}
                                loading={loading}
                            >
                                <EditIcon />
                            </IconButton>
                        </div>
                    )}
                </>
            )}
        </Wrapper>
    )
}

const GET_SLUG_SUGGESTION = gql`
    query GetSlugSuggestion(
        $guid: String
        $title: NonEmptyString!
        $urlSlug: String
    ) {
        entitySlugSuggestion(guid: $guid, title: $title, urlSlug: $urlSlug) {
            available
            suggestion
        }
    }
`

export default TitleSlugField
